import { useNavigate } from 'react-router-dom';
import { Button } from '@vd/ui';
import { ExternalLink } from 'lucide-react';
import settings from '../../routes/settings/Settings.module.css';
import { WidgetNudge } from '../widget/WidgetNudge';
import styles from './WalkthroughRecordingTab.module.css';
import layout from './ExtensionCaptureTab.module.css';
import { CaptureContentsArt, CaptureRouteArt, CaptureWaysArt } from './CaptureIllustrations';

/**
 * How things get into this project, and where the rest of the extension's
 * settings live.
 *
 * Both of those are hard to find out from inside the web app. The extension is
 * a separate surface, and its settings are spread across four places: this
 * project (which pages file here, and what a walkthrough records), the
 * company (per-origin capture and redaction rules), the account (installing
 * and connecting), and the extension's own options page (the per-person
 * choices). Somebody setting a project up has no way to know that, so this
 * page says it.
 */
export function ExtensionCaptureTab({ onOpenTab, onSetupWidget }: {
  /** Switches to another tab in this same page. */
  onOpenTab: (tab: 'urls' | 'recording') => void;
  /** Opens the widget's setup; absent for someone who may not set it up (owners only). */
  onSetupWidget?: () => void;
}) {
  const navigate = useNavigate();

  return (
    <>
      <div className={styles.titleRow}>
        <div>
          <h2 className={settings.h1} style={{ marginBottom: 0 }}>How capture works</h2>
        </div>
        <Button
          variant="primary"
          leftIcon={<ExternalLink size={14} />}
          onClick={() => navigate('/me/settings/extension')}
        >
          Get the extension
        </Button>
      </div>
      <p className={settings.sub}>
        The Toado extension turns a page you are looking at into a ticket, with everything a
        developer would otherwise ask you for. Installing takes a minute and connecting it to
        your account is one approval.
      </p>

      <section className={settings.section}>
        <div className={styles.steps}>
          <div className={layout.stepWithArt}>
            <div className={styles.step}>
              <h3 className={styles.stepTitle}>Two ways to capture</h3>
              <p className={styles.stepBody}>
                <strong>The whole page.</strong> Click the Toado icon and then Capture, or press
                Alt+C. You get one ticket for the page as it is right now.
              </p>
              <p className={styles.stepBody}>
                <strong>A point on the page.</strong> Open Page pins and click the thing you are
                talking about, so the ticket says which element it is about. Recording a
                walkthrough is the same idea at speed: talk once, pin as you go, and every pin
                becomes its own ticket.
              </p>
            </div>
            <div className={layout.stepArt}><CaptureWaysArt /></div>
          </div>

          <div className={layout.stepWithArt}>
            <div className={styles.step}>
              <h3 className={styles.stepTitle}>What comes with it</h3>
              <p className={styles.stepBody}>
                A screenshot, the rendered page, the console, the network requests, any JavaScript
                errors, the viewport size and the browser version. That is the part that makes a
                ticket worth acting on, and it is why a Toado ticket rarely needs a follow-up
                question.
              </p>
              <p className={styles.stepBody}>
                Redaction happens in the browser, before anything is sent. Authorization and
                cookie headers and anything that looks like a token are removed as a matter of
                course, and you can add your own. Per-site rules for what is captured and what is
                scrubbed are company-wide rather than per-project.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/company/settings/capture-profiles')}
                >
                  Capture profiles
                </Button>
              </div>
            </div>
            <div className={layout.stepArt}><CaptureContentsArt /></div>
          </div>

          <div className={layout.stepWithArt}>
            <div className={styles.step}>
              <h3 className={styles.stepTitle}>How a capture reaches this project</h3>
              <p className={styles.stepBody}>
                By the address of the page it came from. When a page matches one of this
                project&rsquo;s URL patterns, captures from it file here without being asked, and
                the in-page pins control opens on its own. Those patterns are only used for that:
                they are not a permission or an allowlist, and they are unrelated to the widget
                setting of a similar shape.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button variant="secondary" size="sm" onClick={() => onOpenTab('urls')}>
                  URL patterns
                </Button>
                <Button variant="secondary" size="sm" onClick={() => onOpenTab('recording')}>
                  Walkthrough recording
                </Button>
              </div>
            </div>
            <div className={layout.stepArt}><CaptureRouteArt /></div>
          </div>

          <div className={styles.step}>
            <h3 className={styles.stepTitle}>Settings that are yours rather than the project&rsquo;s</h3>
            <p className={styles.stepBody}>
              What Alt+C does, the extra headers you scrub, your microphone, and what a
              walkthrough records when the project has no opinion: those live in the
              extension&rsquo;s own options, because they are per person and per browser. Nothing
              you change there affects your teammates.
            </p>
          </div>
        </div>
      </section>

      {onSetupWidget && (
        <section className={settings.section}>
          <WidgetNudge onSetupWidget={onSetupWidget}>
            Add the report widget with one line of code, for the people who don&rsquo;t have the
            extension. The sites you allow it on are added to this project&rsquo;s URL patterns
            too, so the two stay in step.
          </WidgetNudge>
        </section>
      )}
    </>
  );
}
